import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { connect } from 'react-redux';

import {
  isLoggedIn,
  accountDisplayName,
} from '../../../../utils/selectors/account';

import { logout } from '../../../../redux/account';
import {
  openLogInModal,
  openSignUpModal,
  closeNotificationsModal,
  openNotificationsModal,
} from '../../../../redux/modals';

import FlyoutMenuClick from '../../Menus/FlyoutMenuClick';
import FlyoutMenuHover from '../../Menus/FlyoutMenuHover';
import HandOffLink from '../../../../utils/urls/HandOffLink';
import TextButton from '../../Buttons/TextButton';
import AccountActions from '../../DesktopHeader/AccountActions';
import colors from '../../../../styling/colors';
import Bell from '../../../../assets/svg/Bell';
import Dot from '../../../../assets/svg/Dot';
import Heart from '../../../../assets/svg/Heart';
import Notification from '../../DesktopHeader/Notification';

const StyledTextButton = styled(TextButton)`
  color: ${colors('lapel')};
  cursor: pointer;
  font-size: 14px;
  text-decoration: none;
  letter-spacing: 0.6px;
  padding: 0 1rem;

  :last-child {
    border: none;
    padding-right: 0;
  }

  &:hover {
    color: #999;
  }
`;

const FlyoutLink = styled(HandOffLink)`
  font-size: 14px;
  color: ${colors('lapel')};
  text-decoration: none;
  text-align: right;

  text-overflow: ellipsis;
  overflow: hidden;
  left: 0;
  right: 0;
  white-space: nowrap;
  cursor: pointer;

  :hover {
    color: ${colors('dustyGray')};
  }
`;

const AccountActionsContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: flex-end;
`;

const LoggedOutContainer = styled.div``;

const InlineContainer = styled.div`
  display: inline-flex;
  line-height: 1rem;

  & > * {
    display: inline-flex;
    margin: 0 0.5rem;

    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      margin-right: 0;
    }
  }
`;

const IconSection = styled.div`
  display: inline-flex;
  flex: none;
  margin-left: 1rem;
`;

const VerticalRule = styled.div`
  display: inline-block;
  vertical-align: middle;
  background-color: ${({ color }) => color};
  height: ${({ height }) => height};
  width: ${({ width }) => width};
`;

const DotContainer = styled.div`
  position: absolute;
  right: 1px;
  top: -4px;
`;
DotContainer.displayName = 'DotContainer';

const EmptyNotifications = styled.div`
  width: 100%;
  font-size: 0.875rem;
  line-height: 1.25rem;
  padding: 1rem 1rem 0.75rem;
  text-align: center;
`;

export class HeaderAccountSection extends Component {
  static propTypes = {
    openLogInModal: PropTypes.func.isRequired,
    openSignUpModal: PropTypes.func.isRequired,
    account: PropTypes.object,
    logout: PropTypes.func,
    notificationsOpened: PropTypes.func,
    notificationsModalOpen: PropTypes.bool,
    notifications: PropTypes.shape({
      list: PropTypes.arrayOf(PropTypes.object),
      containsNew: PropTypes.bool,
    }),
    isUserLoggedIn: PropTypes.bool,
    accountDisplayName: PropTypes.string,
  };

  render() {
    const {
      account,
      isUserLoggedIn,
      accountDisplayName,
      openLogInModal,
      openSignUpModal,
      openNotificationsModal,
      closeNotificationsModal,
      logout,
      notifications,
      notificationsModalOpen,
    } = this.props;

    const accountFlyoutButton = (
      <FlyoutLink identifier="account-drawer-username" href={'/dashboard'}>
        {accountDisplayName.length > 0 ? accountDisplayName : 'Your account'}
      </FlyoutLink>
    );

    const notificationFlyoutButton = (
      <div>
        {notifications.containsNew ? (
          <DotContainer>
            <Dot
              strokeColor={colors('paloma')}
              height={'0.5rem'}
              width={'0.5rem'}
            />
          </DotContainer>
        ) : null}
        <Bell
          height="1.5rem"
          fillColor={colors('lapel')}
          strokeColor={colors('lapel')}
        />
      </div>
    );

    const notificationContent =
      notifications.list.length > 0 ? (
        notifications.list.map((props, i) => (
          <Notification
            key={i}
            {...props}
            onNotificationClick={closeNotificationsModal}
          />
        ))
      ) : (
        <EmptyNotifications>You have no notifications.</EmptyNotifications>
      );

    const loggedInContent = (
      <AccountActionsContainer>
        <FlyoutMenuHover
          buttonContent={accountFlyoutButton}
          minWidth="10rem"
          width="100%"
          isOverlapped={notificationsModalOpen}
        >
          <AccountActions account={account} logoutAction={logout} />
        </FlyoutMenuHover>
        <VerticalRule color={colors('alto')} width={'1px'} height={'0.75rem'} />
        <IconSection>
          <InlineContainer>
            <HandOffLink href="/cards/favorites" identifier="header_favorites">
              <Heart height="1.5rem" strokeColor={colors('lapel')} />
            </HandOffLink>
            <FlyoutMenuClick
              buttonContent={notificationFlyoutButton}
              minWidth="20rem"
              maxHeight="400px"
              width="100%"
              toggleOn="click"
              onOpen={openNotificationsModal}
              onClose={closeNotificationsModal}
              horizontalAlign="right"
            >
              {notificationContent}
            </FlyoutMenuClick>
          </InlineContainer>
        </IconSection>
      </AccountActionsContainer>
    );

    const loggedOutContent = (
      <LoggedOutContainer>
        <StyledTextButton onClick={openLogInModal}>Login</StyledTextButton>
        <VerticalRule color={colors('alto')} width={'1px'} height={'0.75rem'} />
        <StyledTextButton onClick={openSignUpModal}>Sign up</StyledTextButton>
      </LoggedOutContainer>
    );

    return isUserLoggedIn ? loggedInContent : loggedOutContent;
  }
}

const mapStateToProps = ({ account, notifications, modals }) => ({
  account,
  isUserLoggedIn: isLoggedIn(account),
  accountDisplayName: accountDisplayName(account),
  notifications,
  notificationsModalOpen: modals.notificationsModalOpen,
});

const mapDispatchToProps = {
  openLogInModal,
  openSignUpModal,
  logout,
  closeNotificationsModal,
  openNotificationsModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(HeaderAccountSection);
